'use client'

import { ReactNode, useCallback, useMemo, useState } from 'react'

import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  getRequestReturnDetails,
  transformRequestReturnDetailsResponse,
  RequestReturnDetailsModel,
  ReturnShippingMethodPayingSide,
} from '@marketplace-web/complaints/complaints-data'

import RequestReturnDetailsContext, {
  RequestReturnDetailsContextType,
} from './RequestReturnContext'

type Props = {
  children: ReactNode
}

const RequestReturnDetailsProvider = ({ children }: Props) => {
  const [requestReturnDetails, setRequestReturnDetails] = useState<
    RequestReturnDetailsModel | undefined
  >()
  const [uiState, setUiState] = useState(UiState.Idle)
  const [requestReturnDetailsError, setRequestReturnDetailsError] = useState<string | undefined>(
    undefined,
  )
  const [selectedPaymentSide, setSelectedPaymentSide] =
    useState<ReturnShippingMethodPayingSide | null>(null)

  const [addressError, setAddressError] = useState<string | null>(null)
  const [shippingContactError, setShippingContactError] = useState<ErrorItem | null>(null)

  const fetchRequestReturnDetails = useCallback(
    async ({ transactionId }) => {
      setUiState(UiState.Pending)
      const response = await getRequestReturnDetails(transactionId)

      if ('errors' in response) {
        setRequestReturnDetailsError(response.errors[0]?.value)
        setUiState(UiState.Failure)

        return
      }

      const transformedRequestReturnDetails = transformRequestReturnDetailsResponse(response)

      setRequestReturnDetails(transformedRequestReturnDetails)

      if (transformedRequestReturnDetails.payerSelection?.length) {
        if (!selectedPaymentSide || selectedPaymentSide === ReturnShippingMethodPayingSide.Seller) {
          setSelectedPaymentSide(ReturnShippingMethodPayingSide.Seller)
        } else {
          setSelectedPaymentSide(ReturnShippingMethodPayingSide.Buyer)
        }
      }

      setUiState(UiState.Success)
    },
    [setRequestReturnDetails, setUiState, setRequestReturnDetailsError, selectedPaymentSide],
  )

  const updateSelectedPaymentSide = useCallback(
    (paymentSide: ReturnShippingMethodPayingSide | null) => {
      setSelectedPaymentSide(paymentSide)
    },
    [setSelectedPaymentSide],
  )

  const contextValues = useMemo<RequestReturnDetailsContextType>(
    () => ({
      uiState,
      requestReturnDetailsError,
      setRequestReturnDetailsError,
      requestReturnDetails,
      fetchRequestReturnDetails,
      selectedPaymentSide,
      updateSelectedPaymentSide,
      addressError,
      setAddressError,
      setShippingContactError,
      shippingContactError,
    }),
    [
      uiState,
      requestReturnDetailsError,
      setRequestReturnDetailsError,
      requestReturnDetails,
      fetchRequestReturnDetails,
      selectedPaymentSide,
      updateSelectedPaymentSide,
      addressError,
      setAddressError,
      setShippingContactError,
      shippingContactError,
    ],
  )

  return (
    <RequestReturnDetailsContext.Provider value={contextValues}>
      {children}
    </RequestReturnDetailsContext.Provider>
  )
}

export default RequestReturnDetailsProvider
